import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import DiagnosticsView from './DiagnosticsView';
import { api } from '../services/api';
import {
  DEFAULT_DIAGNOSTICS_THRESHOLDS,
  buildDiagnosticsRows,
  evaluateDevice,
  windowHours,
} from '../utils/diagnosticsHealth';

const STORAGE_KEY = 'nms_diagnostics_settings';
const DEFAULT_RSSI_THRESHOLD_DBM = -75;

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore corrupt data */ }
  return null;
};

const DiagnosticsPage = ({ isActive = true }) => {
  const [saved] = useState(loadSettings);
  const [timeWindow, setTimeWindow] = useState(saved?.window || '24h');
  const [rssiThresholdDbm, setRssiThresholdDbm] = useState(saved?.rssiThresholdDbm ?? DEFAULT_RSSI_THRESHOLD_DBM);
  const [thresholds, setThresholds] = useState({ ...DEFAULT_DIAGNOSTICS_THRESHOLDS, ...(saved?.thresholds || {}) });
  const [diagnostics, setDiagnostics] = useState(null);
  const [devicesBySerial, setDevicesBySerial] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ window: timeWindow, rssiThresholdDbm, thresholds }));
    } catch { /* quota */ }
  }, [timeWindow, rssiThresholdDbm, thresholds]);

  const load = useCallback(async (force = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const [diag, devicesResponse] = await Promise.all([
        api.getNetworkDiagnostics({ window: timeWindow, rssiThresholdDbm, force }),
        api.getDevices({ fields: ['core', 'wifi_interfaces'] }),
      ]);
      const list = devicesResponse?.devices || (Array.isArray(devicesResponse) ? devicesResponse : []);
      const bySerial = {};
      list.forEach((d) => {
        if (d?.serial_number) bySerial[String(d.serial_number).toLowerCase()] = d;
      });
      setDevicesBySerial(bySerial);
      setDiagnostics(diag);
    } catch (err) {
      console.error('Error fetching diagnostics:', err);
      setError(err.message || 'Failed to fetch diagnostics');
    } finally {
      setIsLoading(false);
    }
  }, [timeWindow, rssiThresholdDbm]);

  useEffect(() => {
    if (isActive) load(false);
  }, [isActive, load]);

  const hours = windowHours(timeWindow);
  const allRows = useMemo(
    () => buildDiagnosticsRows(diagnostics, Object.values(devicesBySerial)),
    [diagnostics, devicesBySerial],
  );
  const evaluated = useMemo(() => allRows.map((row) => ({
    row,
    device: devicesBySerial[String(row.device_mac_address || '').toLowerCase()] || null,
    verdicts: evaluateDevice(row, hours, thresholds),
  })), [allRows, devicesBySerial, hours, thresholds]);

  if (error && !diagnostics) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading diagnostics: {error}</Alert>
      </Box>
    );
  }

  if (isLoading && !diagnostics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DiagnosticsView
      diagnostics={diagnostics}
      evaluated={evaluated}
      timeWindow={timeWindow}
      onTimeWindowChange={setTimeWindow}
      rssiThresholdDbm={rssiThresholdDbm}
      onRssiThresholdChange={setRssiThresholdDbm}
      thresholds={thresholds}
      onThresholdsChange={setThresholds}
      isLoading={isLoading}
      error={error}
      onRefresh={() => load(true)}
      devicesBySerial={devicesBySerial}
    />
  );
};

export default DiagnosticsPage;
